import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import "./FilterBar.scss";
import OwButton from "../OwButton/OwButton";
import { FilterBarProps, useFilterBar } from "./useFilterBar";
import { FilterField } from "./FilterField";
import { FilterChips } from "./FilterChips";
import { FilterDrawer } from "./FilterDrawer";
import { PresetTabs } from "./PresetTabs";
import { PresetContextMenu } from "./PresetContextMenu";
import { PresetDeleteConfirm, PresetNameModal } from "./PresetNameModal";
import { PresetManageSidebar } from "./PresetManageSidebar";
import { FeedbackModal } from "./FeedbackModal";
import { getChipLabel, isFieldActive } from "./filterBarUtils";

export type { FilterBarProps, FilterBarMode } from "./useFilterBar";

/**
 * Unified search/filter bar.
 *
 * Auto-selects inline (≤3 filters) or drawer (≥4 filters) mode, owns the filter state, serializes it
 * with buildApiQuery() and emits `onSearch(query)`. Presets are ephemeral until saved and persist
 * only through `onPresetsChange` — this component never touches browser storage.
 *
 * In drawer mode the Refresh + Filters buttons render into the element identified by
 * `actionsContainerId` (page title row) via a portal; without it they render above the preset tabs.
 */
export const FilterBar = (props: FilterBarProps) => {
    const intl = useIntl();
    const bar = useFilterBar(props);
    const { sections, presets, screenKey } = props;

    /** Translate an id when a message exists for it; otherwise return it verbatim (dynamic labels). */
    const translate = useCallback(
        (id: string, values?: { [key: string]: string | number }): string => {
            if (id && intl.messages && intl.messages[id] !== undefined) {
                return intl.formatMessage({ id }, values);
            }

            return id;
        },
        [intl]
    );

    const allLabel = props.allLabel ? translate(props.allLabel) : translate(`all_${screenKey}`);

    // ── Title-row actions (portal target resolved after mount) ──
    const [actionsTarget, setActionsTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!props.actionsContainerId) {
            setActionsTarget(null);
            return;
        }

        setActionsTarget(document.getElementById(props.actionsContainerId));
    }, [props.actionsContainerId]);

    const actions = (
        <div className={"FilterBar-actions d-flex align-items-center"}>
            <OwButton
                title={"refresh"}
                className={"btn-outline-secondary me-2"}
                leftIconClass={"fas fa-sync"}
                fn={bar.refresh}
                active={props.isSearching}
                disableOnActive={true}
                disableWidthOnClick={true}
                testId={"filterbar-refresh"}
            />
            <OwButton
                title={"filters"}
                className={"btn-warning"}
                leftIconClass={"fas fa-filter"}
                fn={bar.openDrawer}
                disableWidthOnClick={true}
                disableDblClickPrevention={true}
                testId={"filterbar-open"}>
                {bar.activeCount > 0 ? <span className={"badge FilterBar-badge ms-2"}>{bar.activeCount}</span> : null}
            </OwButton>
        </div>
    );

    const chips = (
        <FilterChips
            sections={sections}
            fields={bar.allFields}
            applied={bar.applied}
            optionLabels={bar.optionLabels}
            translate={translate}
            onRemove={bar.removeChip}
            onClearAll={bar.clearAll}
        />
    );

    if (bar.resolvedMode === "inline") {
        return (
            <div className={"FilterBar inline d-print-none"}>
                <div className={"FilterBar-inline d-flex flex-wrap align-items-end gap-2"}>
                    {bar.allFields.map((field) => (
                        <div key={field.key} className={"FilterBar-inline-field" + (field.type === "toggle" ? " toggle" : "")}>
                            <label
                                className={"FilterBar-field-label"}
                                htmlFor={field.type === "toggle" ? undefined : `filter-field-${field.key}`}>
                                {translate(field.label)}
                            </label>
                            <FilterField
                                field={field}
                                value={bar.draft[field.key]}
                                onChange={bar.setDraftValue}
                                onEnter={bar.search}
                                translate={translate}
                                optionLabels={bar.optionLabels[field.key]}
                                onOptionLabel={bar.registerOptionLabel}
                                inline={true}
                            />
                        </div>
                    ))}
                    <div className={"d-flex align-items-center"}>
                        <OwButton
                            title={"search"}
                            className={"btn-warning"}
                            leftIconClass={"fas fa-search"}
                            fn={bar.search}
                            active={props.isSearching}
                            disableOnActive={true}
                            disableWidthOnClick={true}
                        />
                        <OwButton title={"reset"} className={"btn-link ms-1"} fn={bar.reset} />
                    </div>
                </div>
                {chips}
            </div>
        );
    }

    const contextPreset = bar.contextMenu ? presets?.find((p) => p.id === bar.contextMenu!.presetId) : undefined;
    const deletePreset = bar.deleteTargetId ? presets?.find((p) => p.id === bar.deleteTargetId) : undefined;

    // "[Orders] Status: Submitted, Customer: 2A GUNS" — prepended to feedback so support knows the context.
    const activeSummary = bar.allFields
        .filter((field) => isFieldActive(field, bar.applied[field.key]))
        .map((field) => {
            const value = getChipLabel(field, bar.applied[field.key], translate, bar.optionLabels[field.key]);
            return value ? `${translate(field.label)}: ${value}` : translate(field.label);
        })
        .join(", ");
    const feedbackContext = `[${translate(screenKey)}] ${activeSummary || translate("no_active_filters")}`;

    return (
        <div className={"FilterBar drawer d-print-none"}>
            {actionsTarget ? createPortal(actions, actionsTarget) : <div className={"d-flex justify-content-end mb-2"}>{actions}</div>}

            {presets ? (
                <PresetTabs
                    allLabel={allLabel}
                    presets={presets}
                    activePresetId={bar.activePresetId}
                    isModified={bar.isModified}
                    isAllDefault={bar.isAllDefault}
                    translate={translate}
                    onSelect={bar.selectPreset}
                    onAdd={() => bar.openSaveAsNew(false)}
                    onManage={bar.openManage}
                    onContextMenu={bar.openContextMenu}
                />
            ) : null}

            {chips}

            <FilterDrawer
                isOpen={bar.drawerOpen}
                sections={sections}
                draft={bar.draft}
                draftActiveCount={bar.draftActiveCount}
                activePreset={bar.activePreset}
                isModified={bar.isModified}
                canUpdatePreset={bar.canUpdatePreset}
                isSearching={props.isSearching}
                optionLabels={bar.optionLabels}
                translate={translate}
                onChange={bar.setDraftValue}
                onOptionLabel={bar.registerOptionLabel}
                onSearch={bar.search}
                onReset={bar.reset}
                onUpdatePreset={bar.updatePreset}
                onSaveAsNew={() => bar.openSaveAsNew(true)}
                onClose={bar.closeDrawer}
                onFeedback={props.onSendFeedback ? bar.openFeedback : undefined}
                isAllDefault={bar.isAllDefault}
                onToggleDefault={() => bar.activePreset && bar.toggleDefault(bar.activePreset.id)}
                onSetAllAsDefault={bar.setAllAsDefault}
                onDeletePreset={bar.requestDeleteFromDrawer}
            />

            {props.onSendFeedback ? (
                <FeedbackModal
                    isOpen={bar.feedbackOpen}
                    context={feedbackContext}
                    translate={translate}
                    onSend={async (text: string) => {
                        await props.onSendFeedback!(`${feedbackContext}\n\n${text}`);
                        bar.closeFeedback();
                    }}
                    onCancel={bar.closeFeedback}
                />
            ) : null}

            {bar.contextMenu && (contextPreset || bar.contextMenu.presetId === null) ? (
                <PresetContextMenu
                    preset={contextPreset || null}
                    isAllDefault={bar.isAllDefault}
                    x={bar.contextMenu.x}
                    y={bar.contextMenu.y}
                    translate={translate}
                    onDelete={bar.requestDelete}
                    onToggleDefault={bar.toggleDefault}
                    onSetAllAsDefault={bar.setAllAsDefault}
                    onManage={presets && presets.length > 0 ? bar.openManage : undefined}
                    onClose={bar.closeContextMenu}
                />
            ) : null}

            {presets ? (
                <PresetManageSidebar
                    isOpen={bar.manageOpen}
                    presets={presets}
                    activePresetId={bar.activePresetId}
                    translate={translate}
                    onReorder={bar.reorderPresets}
                    onRename={bar.renamePreset}
                    onDelete={bar.requestDelete}
                    onToggleDefault={bar.toggleDefault}
                    onClose={bar.closeManage}
                />
            ) : null}

            <PresetNameModal isOpen={bar.nameModalOpen} translate={translate} onSave={bar.saveAsNew} onCancel={bar.closeNameModal} />

            <PresetDeleteConfirm
                isOpen={!!bar.deleteTargetId}
                presetName={deletePreset?.name}
                translate={translate}
                onConfirm={bar.confirmDelete}
                onCancel={bar.cancelDelete}
            />
        </div>
    );
};

export default FilterBar;
